import * as Api from '@ViewModels';
import { useCallback, useState } from 'react';

// v1 supports a single CustomField criterion at a time. If a saved search ever
// contains multiple, only the first is loaded into the editor and the rest will
// be dropped on save. The UI cannot produce that state today, so this is only a
// concern for criteria authored externally. Revisit when multi-row ships.
const findCustomFieldCriterion = (criteria: Api.IContactFilterCriteria[]) =>
	criteria.find(c => c.property === Api.ContactFilterCriteriaProperty.CustomField);

export const useCustomFieldFilters = (initialFilterCriteria: Api.IContactFilterCriteria[] = []) => {
	const initial = findCustomFieldCriterion(initialFilterCriteria);
	const [fieldName, setFieldName] = useState<string>(initial?.values?.FieldName ?? '');
	const [value, setValue] = useState<string>(initial?.value ?? '');

	const getCustomFieldCriteria = useCallback((): Api.IContactFilterCriteria[] => {
		// Backend silently drops criteria with empty FieldName or empty value, but we
		// validate FE-side so the request stays predictable.
		if (!fieldName.trim() || !value.trim()) {
			return [];
		}
		return [
			{
				property: Api.ContactFilterCriteriaProperty.CustomField,
				value: value.trim(),
				values: { FieldName: fieldName },
				op: Api.CONTACT_FILTER_OPERATOR.And,
			},
		];
	}, [fieldName, value]);

	const isCustomFieldCriteria = useCallback(
		(c: Api.IContactFilterCriteria) => c.property === Api.ContactFilterCriteriaProperty.CustomField,
		[]
	);

	const refreshCustomFieldFilters = useCallback((criteria: Api.IContactFilterCriteria[] | null | undefined) => {
		const c = findCustomFieldCriterion(criteria ?? []);
		setFieldName(c?.values?.FieldName ?? '');
		setValue(c?.value ?? '');
	}, []);

	const clearCustomFieldFilters = useCallback(() => {
		setFieldName('');
		setValue('');
	}, []);

	const hasCustomFieldFilters = fieldName.trim() !== '' && value.trim() !== '';

	return {
		fieldName,
		setFieldName,
		value,
		setValue,
		getCustomFieldCriteria,
		isCustomFieldCriteria,
		refreshCustomFieldFilters,
		clearCustomFieldFilters,
		hasCustomFieldFilters,
	};
};
